import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Icon } from "antd";
import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import "./TeamInvitationCard.scss";

function TeamInvitationCard(props) {
  const {
    invitedTeams,
    team,
    isJoined: isJoinedProp,
    selectedTeamId,
    onJoin,
    onReset,
    disabled,
    errorMessage,
    joinErrors,
  } = props;

  // Support both `invitedTeams` (array of teams) and single `team` prop
  const teamsList = invitedTeams || (team ? [team] : []);

  const [isExpanded, setIsExpanded] = useState(!selectedTeamId);

  useEffect(() => {
    // Automatically collapse when a team is selected; re-expand when selection is reset/cleared
    setIsExpanded(!selectedTeamId);
  }, [selectedTeamId]);

  if (isEmpty(teamsList)) {
    return null;
  }

  // --- SINGLE INVITATION VIEW ---
  if (teamsList.length === 1) {
    const singleTeam = teamsList[0];
    const isJoined = Boolean(isJoinedProp || (selectedTeamId && selectedTeamId === singleTeam.id));
    const isOtherTeamSelected = Boolean(selectedTeamId && selectedTeamId !== singleTeam.id);
    const teamName = singleTeam.name || "Unknown Team";
    const managerName = singleTeam.teamManagerName;
    const error = errorMessage || (joinErrors && joinErrors[singleTeam.id]);

    if (isJoined) {
      return (
        <div className="team-invitation-wrapper">
          <div className="team-invitation-card team-invitation-card--joined">
            <div className="team-invitation-card__content">
              <div className="team-invitation-card__title">
                <span className="team-invitation-card__checkmark">&#10003;</span>
                You have joined {teamName}
              </div>
              <div className="team-invitation-card__description">
                {managerName ? `Team Manager: ${managerName}. ` : ""}Once your registration is submitted, you will
                appear on the team roster.
              </div>
            </div>
            {onReset && !disabled && (
              <div className="team-invitation-card__action">
                <Button size="small" onClick={onReset} className="team-invitation-multi__change-button">
                  Change Team
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="team-invitation-wrapper">
        <div
          className={classnames("team-invitation-card team-invitation-card--pending", {
            "team-invitation-card--dimmed": isOtherTeamSelected,
          })}
        >
          <div className="team-invitation-card__content">
            <div className="team-invitation-card__title">You have been invited to join {teamName}</div>
            {managerName && <div className="team-invitation-card__manager">Team Manager: {managerName}</div>}
            {isOtherTeamSelected && (
              <div className="team-invitation-card__muted-hint">
                You have selected another team for this competition. Clear your team selection to join this team.
              </div>
            )}
          </div>
          <div className="team-invitation-card__action">
            <Button
              type="primary"
              className="team-invitation-card__join-button"
              onClick={() => onJoin(singleTeam)}
              disabled={disabled || isOtherTeamSelected || Boolean(singleTeam.isFull)}
            >
              Join Team
            </Button>
          </div>
        </div>
        {error && <Alert message={error} type="error" showIcon className="team-invitation-card__error-alert" />}
      </div>
    );
  }

  // --- MULTIPLE INVITATIONS (OPTION 3 HYBRID COLLAPSIBLE) ---
  const joinedInvitedTeam = teamsList.find(t => t.id === selectedTeamId);
  const hasJoinedInvitedTeam = Boolean(joinedInvitedTeam);
  const hasSelectedOtherTeam = Boolean(selectedTeamId && !joinedInvitedTeam);
  const isAnyTeamSelected = Boolean(selectedTeamId);

  return (
    <div
      className={classnames("team-invitation-wrapper team-invitation-wrapper--multi", {
        "team-invitation-wrapper--joined": hasJoinedInvitedTeam,
        "team-invitation-wrapper--muted": hasSelectedOtherTeam,
      })}
    >
      {/* 1. Header or Summary Banner */}
      {hasJoinedInvitedTeam && (
        <div className="team-invitation-card team-invitation-card--joined team-invitation-multi__summary">
          <div className="team-invitation-card__content">
            <div className="team-invitation-card__title">
              <span className="team-invitation-card__checkmark">&#10003;</span>
              You have joined {joinedInvitedTeam.name}
            </div>
            <div className="team-invitation-card__description">
              {joinedInvitedTeam.teamManagerName ? `Team Manager: ${joinedInvitedTeam.teamManagerName}. ` : ""}
              Once your registration is submitted, you will appear on the team roster.
            </div>
          </div>
          <div className="team-invitation-multi__summary-actions">
            {onReset && !disabled && (
              <Button size="small" onClick={onReset} className="team-invitation-multi__change-button">
                Change Team
              </Button>
            )}
            <Button
              type="link"
              size="small"
              onClick={() => setIsExpanded(prev => !prev)}
              className="team-invitation-multi__toggle-button"
            >
              {isExpanded ? (
                <span>
                  Hide other invitations <Icon type="up" />
                </span>
              ) : (
                <span>
                  View other invitations ({teamsList.length - 1}) <Icon type="down" />
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {hasSelectedOtherTeam && (
        <div className="team-invitation-card team-invitation-card--muted team-invitation-multi__summary">
          <div className="team-invitation-card__content">
            <div className="team-invitation-card__title">
              <Icon type="info-circle" style={{ color: "#faad14", marginRight: 8 }} />
              You have {teamsList.length} pending team invitations (Inactive)
            </div>
            <div className="team-invitation-card__description">
              You selected another team for this competition. Reset your team selection above to join an invited team.
            </div>
          </div>
          <div className="team-invitation-multi__summary-actions">
            <Button
              type="link"
              size="small"
              onClick={() => setIsExpanded(prev => !prev)}
              className="team-invitation-multi__toggle-button"
            >
              {isExpanded ? (
                <span>
                  Hide invitations <Icon type="up" />
                </span>
              ) : (
                <span>
                  View invitations ({teamsList.length}) <Icon type="down" />
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {!isAnyTeamSelected && (
        <div className="team-invitation-multi__header">
          <div className="team-invitation-multi__header-left">
            <div className="team-invitation-multi__title">
              <Icon type="mail" style={{ color: "#1890ff", marginRight: 8 }} />
              You have received {teamsList.length} team invitations
            </div>
            <div className="team-invitation-multi__subtitle">
              You can join only one team for this competition. Select the team you want to join:
            </div>
          </div>
          <div className="team-invitation-multi__header-right">
            <Button
              type="link"
              size="small"
              onClick={() => setIsExpanded(prev => !prev)}
              className="team-invitation-multi__toggle-button"
            >
              {isExpanded ? (
                <span>
                  Collapse <Icon type="up" />
                </span>
              ) : (
                <span>
                  Show ({teamsList.length}) <Icon type="down" />
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* 2. Expanded List Content */}
      {isExpanded && (
        <div
          className={classnames("team-invitation-multi__list-container", {
            "team-invitation-multi__list-container--disabled": isAnyTeamSelected,
          })}
        >
          {isAnyTeamSelected && (
            <div className="team-invitation-multi__disabled-notice">
              <Alert
                message="You can only join one team per competition. To choose a different team, clear or reset the 'Find your team' search field above."
                type="info"
                showIcon
              />
            </div>
          )}

          <div className="team-invitation-multi__list">
            {teamsList.map(teamItem => {
              const isItemJoined = teamItem.id === selectedTeamId;
              const isItemDisabled = disabled || (isAnyTeamSelected && !isItemJoined) || Boolean(teamItem.isFull);
              const error = joinErrors && joinErrors[teamItem.id];

              return (
                <div
                  key={teamItem.id}
                  className={classnames("team-invitation-multi__row", {
                    "team-invitation-multi__row--joined": isItemJoined,
                    "team-invitation-multi__row--dimmed": isAnyTeamSelected && !isItemJoined,
                  })}
                >
                  <div className="team-invitation-multi__row-main">
                    <div className="team-invitation-multi__row-info">
                      <div className="team-invitation-multi__row-name">
                        {isItemJoined && <span className="team-invitation-card__checkmark">&#10003; </span>}
                        {teamItem.name || "Unknown Team"}
                      </div>
                      {teamItem.teamManagerName && (
                        <div className="team-invitation-multi__row-manager">
                          Team Manager: {teamItem.teamManagerName}
                        </div>
                      )}
                      {teamItem.isFull && (
                        <div className="team-invitation-multi__row-full-warning">Team is currently full</div>
                      )}
                    </div>

                    <div className="team-invitation-multi__row-action">
                      {isItemJoined ? (
                        <span className="team-invitation-multi__joined-tag">&#10003; Joined</span>
                      ) : (
                        <Button
                          type="primary"
                          className="team-invitation-card__join-button"
                          onClick={() => onJoin(teamItem)}
                          disabled={isItemDisabled}
                        >
                          Join Team
                        </Button>
                      )}
                    </div>
                  </div>

                  {error && (
                    <Alert message={error} type="error" showIcon className="team-invitation-card__error-alert" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

TeamInvitationCard.propTypes = {
  invitedTeams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      teamManagerName: PropTypes.string,
      isFull: PropTypes.bool,
      calculatedAgeGroupName: PropTypes.string,
    }),
  ),
  team: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    teamManagerName: PropTypes.string,
    isFull: PropTypes.bool,
  }),
  isJoined: PropTypes.bool,
  selectedTeamId: PropTypes.string,
  onJoin: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  joinErrors: PropTypes.objectOf(PropTypes.string),
};

TeamInvitationCard.defaultProps = {
  invitedTeams: null,
  team: null,
  isJoined: false,
  selectedTeamId: undefined,
  onReset: null,
  disabled: false,
  errorMessage: null,
  joinErrors: {},
};

export default TeamInvitationCard;
